import { makeAutoObservable } from 'mobx';
import axios from 'axios';
import { RootStore } from './RootStore';
import i18next from '../lang';

interface User {
  id: string;
  username: string;
  email: string;
  level: string;
  organizationName?: string;
}

export class AuthStore {
  user: User | null = null;
  isLoading = false;
  error: string | null = null;
  isInitialized = false;
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    this.initializeAuth();
  }

  private async initializeAuth() {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await this.fetchUserInfo();
      } catch (error) {
        this.logout();
      }
    }
    this.isInitialized = true;
  }

  setUser(user: User | null) {
    this.user = user;
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  async login(identifier: string, password: string) {
    this.setLoading(true);
    this.setError(null);

    try {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);

      const response = await axios.post('/api/v1/auth/login', {
        ...(isEmail ? { email: identifier } : { id: identifier }),
        password
      });

      if (response.data?.result === 'SUCCESS') {
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        this.setUser(user);
      }

      return response.data;
    } catch (error: any) {
      let errorMessage = i18next.t('error.login_failed');

      if (error.response) {
        if (typeof error.response.data === 'string') {
          errorMessage = i18next.t(error.response.data);
        } else if (error.response.data?.message) {
          errorMessage = i18next.t(error.response.data.message);
        } else if (error.response.data?.error) {
          errorMessage = i18next.t(error.response.data.error);
        }
      }

      this.setError(errorMessage);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async fetchUserInfo() {
    try {
      const response = await axios.get('/api/v1/auth/me');
      if (response.data && typeof response.data === 'object') {
        this.setUser(response.data);
      } else {
        throw new Error(i18next.t('error.invalid_user_data'));
      }
    } catch (error: any) {
      let errorMessage = i18next.t('error.user_info_fetch_failed');
      if (error.response?.data?.message) {
        errorMessage = i18next.t(error.response.data.message);
      }
      throw new Error(errorMessage);
    }
  }

  logout() {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    this.setUser(null);
    this.setError(null);
  }

  get isAuthenticated() {
    return !!this.user;
  }
}